import { useMemo } from 'react'
import { formatBasisPoints } from '@klear/basis-points/boundary'
import { PERMISSION_ACTION_VIEW, PERMISSION_ENTITY_INVOICE_OFFER } from '@klear/constants'
import { Money } from '@klear/money'
import type { Fee } from '@klear/fee'
import type { InvoiceOffer } from '@klear/invoice-offer/invoice-offer'
import { invoiceOfferAdvanceRateFormatted } from '@klear/invoice-offer/list-display'
import { KlearDate } from '@klear/date'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useUnpaidLateFeesByOffer } from '@klear/ui/hooks/use-collections'

function FeeBreakdown({
  platformFee,
  advance,
  isAdminContext,
  creditClassification,
  daysToRecovery
}: {
  platformFee: Fee
  advance: Money
  isAdminContext: boolean
  creditClassification?: string
  daysToRecovery: number
}) {
  if (!isAdminContext) return null

  const klearIsLoss = platformFee.klearAmount.isNegative()

  return (
    <div className="p-4 bg-muted rounded-lg space-y-2">
      <h4 className="font-medium">Calculated Fee Breakdown</h4>
      <div className="grid grid-cols-3 text-sm" style={{ columnGap: '2rem', rowGap: '0.5rem' }}>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Classification:</span>
          <span className="font-mono text-right">{creditClassification || 'N/A'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Rating Used:</span>
          <span className="font-mono text-right">
            {platformFee.ratingUsed} ({platformFee.ratingAgency ?? 'N/A'})
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Tier:</span>
          <span className="font-mono text-right">{platformFee.tierUsed}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">SPV Rate:</span>
          <span className="font-mono text-blue-600 text-right">
            {formatBasisPoints(platformFee.spvRateUsed)} → {platformFee.spvAmount.toString()}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Klear Rate:</span>
          <span className={`font-mono text-right ${klearIsLoss ? 'text-red-600' : 'text-green-600'}`}>
            {formatBasisPoints(platformFee.klearRateUsed)} → {platformFee.klearAmount.toString()}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Annualized Rate (Pricing Card):</span>
          <span className="font-mono font-semibold text-right">{formatBasisPoints(platformFee.rateUsed)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Advance Amount:</span>
          <span className="font-mono text-right">{advance.toString()}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Days to Recovery:</span>
          <span className="font-mono text-right">{daysToRecovery}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Total Fee:</span>
          <span className="font-mono font-medium text-right">{platformFee.amount.toString()}</span>
        </div>
      </div>
    </div>
  )
}

function daysBetween(start: KlearDate | undefined, end: KlearDate | undefined): number {
  if (start == null || end == null) return 0
  return KlearDate.daysBetween(start, end)
}

export interface InvoiceOfferViewProps {
  offer: InvoiceOffer
  fees: Fee[]
  isAdminContext?: boolean
  creditClassification?: string
}

export function InvoiceOfferView({ offer, fees, isAdminContext = true, creditClassification }: InvoiceOfferViewProps) {
  const { user } = useAuth()
  const canViewUnpaidLateFees = user.hasPermission(PERMISSION_ENTITY_INVOICE_OFFER, PERMISSION_ACTION_VIEW)
  const lateFeeTraderId = offer.traderId > 0 ? offer.traderId : undefined
  const { rows: unpaidLateFees } = useUnpaidLateFeesByOffer(
    lateFeeTraderId,
    undefined,
    canViewUnpaidLateFees && lateFeeTraderId != null
  )

  const platformFee = useMemo((): Fee | undefined => {
    return fees.find((f) => f.type === 'platform_fee') ?? fees[0]
  }, [fees])

  const totalFeeMoney = useMemo(() => {
    const amounts = fees.map((f) => f.amount)
    return amounts.length > 0 ? Money.sum(amounts) : null
  }, [fees])

  const lateFeeRemaining = unpaidLateFees.find((row) => row.offerId === offer.id)?.remaining

  const hasFeeBreakdown = platformFee != null && platformFee.ratingUsed != null && platformFee.tierUsed != null

  const pricingDaysToRecovery =
    platformFee?.effectiveDate && platformFee?.endDate
      ? daysBetween(platformFee.effectiveDate, platformFee.endDate)
      : daysBetween(offer.effectiveDate, offer.endDate)

  const feeCell = totalFeeMoney != null && !totalFeeMoney.isZero() ? totalFeeMoney.toString() : '—'

  const fields: { label: string; value: string }[] = [
    { label: 'Purchase Price', value: offer.purchasePrice.toString() },
    { label: 'Advance Rate', value: invoiceOfferAdvanceRateFormatted(offer) },
    { label: 'Total Fees', value: feeCell },
    ...(lateFeeRemaining != null && lateFeeRemaining.isPositive()
      ? [{ label: isAdminContext ? 'Late Fees' : 'Extension Fees', value: lateFeeRemaining.toString() }]
      : []),
    ...(isAdminContext ? [{ label: 'Offer Date', value: offer.effectiveDate.toString() }] : []),
    { label: 'Due Date', value: offer.endDate?.toString() ?? '—' },
    ...(isAdminContext
      ? [
          { label: 'Settlement Collection', value: offer.settlementCollection.toString() },
          { label: 'Settlement Advance Recovery', value: offer.settlementAdvanceRecovery.toString() }
        ]
      : []),
    { label: 'Settlement Net', value: offer.settlementNetAmount.toString() }
  ]

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        {fields.map((f) => (
          <div key={f.label} className="min-w-0">
            <span className="whitespace-nowrap text-muted-foreground">{f.label}:</span>{' '}
            <span className="font-semibold">{f.value}</span>
          </div>
        ))}
      </div>
      {isAdminContext ? null : (
        <p className="text-xs text-muted-foreground">
          We collect {offer.settlementCollection.toString()} from your customer and recover the{' '}
          {offer.settlementAdvanceRecovery.toString()} advance.
        </p>
      )}

      {hasFeeBreakdown && platformFee != null && (
        <FeeBreakdown
          platformFee={platformFee}
          advance={offer.purchasePrice}
          isAdminContext={isAdminContext}
          creditClassification={creditClassification}
          daysToRecovery={pricingDaysToRecovery}
        />
      )}
    </div>
  )
}
